import {
  CropRect,
  EditRecipe,
  NEUTRAL_RECIPE,
  PerspectiveCorners,
  Point,
  editRecipeSchema,
} from "./edit-recipe.schema";

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function cropEqual(a: CropRect | null, b: CropRect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

function pointEqual(a: Point, b: Point): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

function perspectiveEqual(
  a: PerspectiveCorners | null,
  b: PerspectiveCorners | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    pointEqual(a[0], b[0]) &&
    pointEqual(a[1], b[1]) &&
    pointEqual(a[2], b[2]) &&
    pointEqual(a[3], b[3])
  );
}

/** Compare deux recettes champ par champ (pas de stringify). */
export function recipesEqual(a: EditRecipe, b: EditRecipe): boolean {
  return (
    a.version === b.version &&
    a.rotate === b.rotate &&
    a.flipH === b.flipH &&
    a.flipV === b.flipV &&
    a.contrast === b.contrast &&
    a.density === b.density &&
    a.sharpen === b.sharpen &&
    cropEqual(a.crop, b.crop) &&
    perspectiveEqual(a.perspective, b.perspective)
  );
}

export function isNeutralRecipe(recipe: EditRecipe): boolean {
  return recipesEqual(recipe, NEUTRAL_RECIPE);
}

/**
 * Premier enregistrement d'une retouche : `editRecipe` persisté est absent.
 * Le converter s'en sert pour figer la source non-HD (`-large.webp` → `-source.webp`).
 */
export function isFirstEdit(savedRecipe: unknown): boolean {
  return savedRecipe == null;
}

/**
 * Parse une recette persistée (JSON Prisma / callback). Recette invalide ou
 * absente → `NEUTRAL_RECIPE` (l'éditeur reste utilisable).
 */
export function parseEditRecipe(input: unknown): EditRecipe {
  if (input == null) return { ...NEUTRAL_RECIPE };
  const parsed = editRecipeSchema.safeParse(input);
  return parsed.success ? parsed.data : { ...NEUTRAL_RECIPE };
}

/** Borne les scalaires dans les limites du schéma. */
export function clampRecipe(recipe: EditRecipe): EditRecipe {
  return {
    version: 1,
    crop: recipe.crop,
    perspective: recipe.perspective,
    flipH: recipe.flipH ?? false,
    flipV: recipe.flipV ?? false,
    rotate: clamp(recipe.rotate, -360, 360),
    contrast: clamp(recipe.contrast, -100, 100),
    density: clamp(recipe.density, -100, 100),
    sharpen: clamp(recipe.sharpen, 0, 500),
  };
}

const FULL_CROP_EPS = 0.005;
const IDENTITY_CORNERS: PerspectiveCorners = [
  [0, 0],
  [1, 0],
  [1, 1],
  [0, 1],
];

export function isFullCrop(crop: CropRect | null): boolean {
  if (!crop) return true;
  return (
    crop.x <= FULL_CROP_EPS &&
    crop.y <= FULL_CROP_EPS &&
    crop.w >= 1 - FULL_CROP_EPS &&
    crop.h >= 1 - FULL_CROP_EPS
  );
}

export function isIdentityPerspective(
  corners: PerspectiveCorners | null,
): boolean {
  if (!corners) return true;
  return perspectiveEqual(corners, IDENTITY_CORNERS);
}

/** Compacte une recette avant envoi : identity crop/perspective → null. */
export function normalizeRecipe(recipe: EditRecipe): EditRecipe {
  const rotate = ((recipe.rotate % 360) + 360) % 360;
  const signedRotate = rotate > 180 ? rotate - 360 : rotate;
  return clampRecipe({
    ...recipe,
    rotate: signedRotate === 0 ? 0 : signedRotate,
    crop: isFullCrop(recipe.crop) ? null : recipe.crop,
    perspective: isIdentityPerspective(recipe.perspective)
      ? null
      : recipe.perspective,
  });
}

/**
 * Fusionne un patch dans une recette, borne les scalaires, valide via zod.
 * En cas d'échec (crop/perspective hors contrat), renvoie `base` inchangé.
 */
export function applyRecipePatch(
  base: EditRecipe,
  patch: Partial<Omit<EditRecipe, "version">>,
): EditRecipe {
  const next = clampRecipe({ ...base, ...patch, version: 1 });
  const parsed = editRecipeSchema.safeParse(next);
  return parsed.success ? parsed.data : base;
}
